import React, { useEffect, useState } from "react";
import CreateMcq from "./CreateMcq";
import CreateUploadType from "./CreateUploadType";
import axios from "../../axios";
import Guides from "./Guides";
import { useSelector } from "react-redux";
import ComponentType from "../../Enum/ComponentTypeEnum";
import CheckValidationInput from "./CheckValidationInput";
import { Info } from "lucide-react";
import { IP } from "../../internalServer";
import { iconData } from "../../iconData";
import assets from "../../assets/assets";
import QuestionTypePopup from "./QuestionTypePopup";
import PdfViewer from "./PdfViewer";
import { hasDescriptiveAutoAssessment } from "../../Utils/features";

const DefaultQuestion = ({
  componentType,
  nextQuestion,
  question,
  setQuestions,
  assessmentDetails,
  qArr,
  showErrorInputField,
  setShowErrorInputField,
  getErrorMsg,
  chapter,
  selectedBox,
  isActive,
  onToggle,
  questionType,
}) => {
  let teacher_id = JSON.parse(localStorage.getItem("Teacher_id"));
  const timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );

  const skillsData = useSelector((state) => state.initialData.skills);
  const difficulty_levelData = useSelector(
    (state) => state.initialData.difficultyLevels,
  );
  const domainsData = useSelector((state) => state.initialData.domain);

  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");
  const [fLimit, setFLimit] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isValidFileType, setIsValidFileType] = useState(true);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [dropdownList, setDropdownList] = useState({
    skills: skillsData,
    types: questionType,
    domains: null,
    chapters: chapter,
    difficultyLevels: difficulty_levelData,
  });
  const { skills, types, domains, chapters, difficultyLevels } = dropdownList;
  const [zoomedAttachment, setZoomedAttachment] = useState(false);
  const [click, setClick] = useState(false);
  const [openQuestionsPopup, setOpenQuestionsPopup] = useState(false);

  useEffect(() => {
    setDropdownList({
      skills: skillsData,
      types: questionType,
      domains: domainsData,
      chapters: chapter,
      difficultyLevels: difficulty_levelData,
    });
  }, [skillsData, questionType, domainsData, difficulty_levelData, chapter]);

  const skillToDomainNameMap = {
    Remember: "Factual",
    Understand: "Conceptual",
    Apply: "Conceptual",
    Analyze: "Procedural",
    Evaluate: "Metacognitive",
    Create: "Metacognitive",
    None: "None",
  };

  const domainNameToIdMap = Object.fromEntries(
    Array.isArray(domains) ? domains.map((d) => [d.name, d.id]) : [],
  );

  const skillsWithDomainId =
    Array.isArray(skills) &&
    skills.map((skill) => {
      const domainName = skillToDomainNameMap[skill.name];
      const domainId = domainNameToIdMap[domainName] || null;
      return { ...skill, domain_id: domainId };
    });

  const isTitleMissing = !question?.title;
  const isMarksMissing = !question?.correct_score;
  const isTypeMissing = !question?.type_id;
  const isSkillMissing = !question?.skill_id;
  const isChapterMissing = !question?.chapter_id;
  const isDifficultyMissing = !question?.difficulty_level;

  const errorInputStyle = {
    border: "1px solid #FCA5A5",
    backgroundColor: "#FEF2F2",
  };
  const normalInputStyle = {
    border: "1px solid #D1D5DB",
    backgroundColor: "#fff",
  };

  // ==========================================
  // File validation (purana logic)
  // ==========================================
  const validateFileType = (file) => {
    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.pdf|\.doc|\.docx|\.txt)$/i;
    if (
      !allowedExtensions.test(file.name) ||
      question?.attachments?.length >= 5
    ) {
      setIsValidFileType(false);
      return false;
    }
    setIsValidFileType(true);
    return true;
  };

  // ==========================================
  // Upload attachment
  // ==========================================
  const handleAttachment = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFLimit(true);
      return;
    }

    if (validateFileType(file)) {
      try {
        setLoader(true);
        let formData = new FormData();
        formData.append("name", file.name);
        formData.append("uploadQuestionAttachment", file);
        let res = await axios.post(
          `/api/v1/question/uploadAttachment/teacherId/${teacher_id}`,
          formData,
        );
        const attachments_sizeKb = [Math.round(file.size / 1024)];
        const attachments = [res.data.data];
        res && setFLimit(false);
        setLoader(false);
        if (attachments.length > 0) {
          setQuestions((prev) => {
            const updated = {
              ...prev[nextQuestion],
              is_question_edited: true,
              attachments: [
                ...(prev[nextQuestion].attachments || []),
                ...attachments,
              ],
              attachments_sizeKb,
            };
            const newQ = [...prev];
            newQ[nextQuestion] = updated;
            return newQ;
          });
        }
      } catch (error) {
        console.log(error);
        setLoader(false);
      }
    }
  };

  const checkRemoveAttachmentisFromLibrary = (attachment, idx) => {
    if (question.id) handleSoftDeleteAttachment(attachment, idx);
    else handleRemoveAttachment(attachment, idx);
  };

  const handleSoftDeleteAttachment = (attachment, idx) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[nextQuestion] = {
        ...updated[nextQuestion],
        attachments: updated[nextQuestion].attachments.filter(
          (_, i) => i !== idx,
        ),
        is_question_edited: true,
      };
      return updated;
    });
  };

  const handleRemoveAttachment = async (attachmentToRemove, idx) => {
    try {
      const filePath = attachmentToRemove?.url || attachmentToRemove;
      const res = await axios.delete(`/api/v1/file/delete`, {
        data: { file_path: filePath },
      });
      if (res) {
        setQuestions((prev) => {
          const updated = [...prev];
          updated[nextQuestion] = {
            ...updated[nextQuestion],
            attachments: updated[nextQuestion].attachments.filter(
              (_, i) => i !== idx,
            ),
            is_question_edited: true,
          };
          return updated;
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = "Unsaved changes!";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  const handleClickforpdf = (fileUrl) => {
    setResponseAttach(fileUrl);
    setMideaviewer(true);
  };

  return (
    <>
      <div className="w-100">
        <div
          style={{
            backgroundColor: "#F9FAFB",
            padding: "16px",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
          }}
        >
          {/* ========================================== */}
          {/* Question Text + Attachments */}
          {/* ========================================== */}
          <div className="mb-3">
            <label
              className="poppins-semibold"
              style={{
                fontSize: "12px",
                color: "#4B5563",
                marginBottom: "4px",
                display: "block",
              }}
            >
              Question Text *
            </label>
            <div className="d-flex align-items-start gap-2">
              <textarea
                className="rounded-2 p-2"
                style={{
                  width: "95%",
                  fontSize: "13.5px",
                  outline: "none",
                  ...(isTitleMissing ? errorInputStyle : normalInputStyle),
                  resize: "vertical",
                  minHeight: "60px",
                }}
                placeholder="Enter question here..."
                value={question.title || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setUnsavedChanges(true);
                  setQuestions((prev) => {
                    const updated = {
                      ...prev[nextQuestion],
                      title: value,
                      is_question_edited: true,
                    };
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
              />
              <div
                style={{
                  width: "5%",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "6px",
                }}
              >
                {(!question?.attachments ||
                  question.attachments.length < 5) &&
                  !loader && (
                    <>
                      <input
                        onChange={(e) => handleAttachment(e.target.files)}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        type="file"
                        id={`attachFile${nextQuestion}`}
                        style={{ display: "none" }}
                      />
                      <label
                        htmlFor={`attachFile${nextQuestion}`}
                        className="rounded-2 d-flex align-items-center justify-content-center"
                        style={{
                          border: "1px dashed #D1D5DB",
                          cursor: "pointer",
                          width: "36px",
                          height: "36px",
                        }}
                        title="Attach file"
                      >
                        <img
                          src={assets.UploadIcon}
                          width="18px"
                          alt="Attach"
                        />
                      </label>
                    </>
                  )}
                {loader && (
                  <img
                    style={{ width: "20px" }}
                    src="/gif/Loader.gif"
                    alt="Loading"
                  />
                )}
              </div>
            </div>

            {/* 👈 ATTACHMENT PREVIEW — purani functionality */}
            {question?.attachments && question.attachments.length > 0 && (
              <div className="mt-2">
                <div className="d-flex flex-wrap gap-2">
                  {question.attachments.map((attachment, idx) => {
                    const fileUrl =
                      typeof attachment === "string"
                        ? attachment
                        : attachment?.url;
                    if (!fileUrl) return null;

                    const fileType = fileUrl.split(".").pop().toLowerCase();
                    const isImage = ["jpg", "jpeg", "png"].includes(fileType);
                    const isVideo = ["mp4", "mov"].includes(fileType);
                    const isPdf = ["pdf"].includes(fileType);
                    const fileIcon =
                      iconData?.find((i) => i.extension === fileType)?.icon ||
                      "";
                    const fullUrl = fileUrl.startsWith("http")
                      ? fileUrl
                      : `${IP}${fileUrl}`;
                    const fileName = fileUrl.replace(/^[^_]*_/, "");

                    return (
                      <div
                        key={idx}
                        style={{
                          position: "relative",
                          borderRadius: "8px",
                          border: "1px solid #E5E7EB",
                          backgroundColor: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        {/* Image */}
                        {isImage ? (
                          <img
                            src={fullUrl}
                            alt="attachment"
                            onClick={() => setZoomedAttachment(fullUrl)}
                            style={{
                              width: "80px",
                              height: "72px",
                              objectFit: "cover",
                              cursor: "pointer",
                              display: "block",
                            }}
                          />
                        ) : isVideo ? (
                          <video
                            src={fullUrl}
                            style={{
                              width: "80px",
                              height: "72px",
                              objectFit: "cover",
                              display: "block",
                            }}
                            controls
                          />
                        ) : isPdf ? (
                          <div
                            onClick={() => handleClickforpdf(fileUrl)}
                            style={{
                              width: "110px",
                              height: "72px",
                              background: "#f5f5f5",
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "4px",
                            }}
                          >
                            <img
                              src={fileIcon}
                              alt="pdf"
                              style={{ width: "28px", height: "28px" }}
                            />
                            <span
                              style={{
                                fontSize: "9px",
                                color: "#6B7280",
                                marginTop: "2px",
                                maxWidth: "100px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={fileName}
                            >
                              {fileName}
                            </span>
                          </div>
                        ) : (
                          <a
                            href={`https://docs.google.com/gview?url=${encodeURIComponent(
                              fullUrl,
                            )}&embedded=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            <div
                              style={{
                                width: "110px",
                                height: "72px",
                                background: "#f5f5f5",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px",
                              }}
                            >
                              <img
                                src={fileIcon}
                                alt="file"
                                style={{ width: "28px", height: "28px" }}
                              />
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: "#6B7280",
                                  marginTop: "2px",
                                  maxWidth: "100px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={fileName}
                              >
                                {fileName}
                              </span>
                            </div>
                          </a>
                        )}

                        {/* Remove (×) button */}
                        <img
                          onClick={() =>
                            checkRemoveAttachmentisFromLibrary(attachment, idx)
                          }
                          src="/images/attachment_cross_icon.png"
                          alt="Remove"
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            cursor: "pointer",
                            width: "14px",
                            height: "14px",
                            backgroundColor: "rgba(255,255,255,0.9)",
                            borderRadius: "50%",
                            padding: "2px",
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error messages */}
            <span style={{ color: "red", fontSize: "10px" }}>
              {fLimit && "Max file size: 10 MB"}
            </span>
            {!isValidFileType && (
              <p
                style={{
                  fontSize: "11px",
                  color: "#DC2626",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                Invalid file type. Allowed: pdf, jpg, jpeg, png, doc, docx. Max
                5 files.
              </p>
            )}
          </div>

          {/* ========================================== */}
          {/* Compact Grid — Question Type & Marks */}
          {/* ========================================== */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label
                className="poppins-semibold"
                style={{
                  fontSize: "12px",
                  color: "#4B5563",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Question Type *
              </label>
              <select
                className="form-select rounded-2"
                style={{
                  fontSize: "13px",
                  ...(isTypeMissing ? errorInputStyle : normalInputStyle),
                  padding: "6px 12px",
                }}
                onChange={(e) => {
                  const selectedOption = e.target.options[e.target.selectedIndex];
                  const isManuallyGraded =
                    selectedOption.getAttribute("data-is-manually-graded") ===
                    "true";

                  setQuestions((prev) => {
                    const current = prev[nextQuestion];
                    let updated = {
                      ...current,
                      type_id: e.target.value,
                      question_type: String(isManuallyGraded),
                      is_question_edited: true,
                    };
                    if (isManuallyGraded) {
                      updated.options = [];
                      updated.correct_answers = [
                        {
                          reason_for_answer: {
                            explanation: null,
                            attachments: null,
                          },
                        },
                      ];
                      if (hasDescriptiveAutoAssessment)
                        updated.subjective_evaluation = {
                          enabled: false,
                          expectedAnswer: "",
                          rubric: [],
                          ...(current.subjective_evaluation || {}),
                        };
                    } else {
                      updated.options = [
                        { key: "A", value: null },
                        { key: "B", value: null },
                      ];
                      delete updated.subjective_evaluation;
                    }
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
                value={
                  question?.type_id != null && question?.type_id !== ""
                    ? String(question.type_id)
                    : ""
                }
                disabled={(() => {
                  const isAdaptiveMode =
                    selectedBox === "adaptive" ||
                    selectedBox?.adaptive ||
                    selectedBox?.isAdaptive;
                  const isAutoEvalMode =
                    selectedBox === "auto-evaluate" ||
                    selectedBox?.autoEvaluate === true;
                  if (isAdaptiveMode) return true;
                  if (isAutoEvalMode) return !hasDescriptiveAutoAssessment;
                  return false;
                })()}
              >
                <option value="">Select Type</option>
                {(() => {
                  const isAutoMode =
                    selectedBox === "adaptive" ||
                    selectedBox === "auto-evaluate" ||
                    selectedBox?.adaptive ||
                    selectedBox?.isAdaptive ||
                    selectedBox?.autoEvaluate;
                  const visibleTypes = (() => {
                    if (hasDescriptiveAutoAssessment) return types || [];
                    if (isAutoMode)
                      return (types || []).filter((t) => t.name === "MCQ");
                    return types || [];
                  })();
                  return visibleTypes.map((type, index) => (
                    <option
                      key={index}
                      value={String(type.id)}
                      data-is-manually-graded={String(
                        type?.is_manually_graded,
                      )}
                    >
                      {type.name}
                    </option>
                  ));
                })()}
              </select>
            </div>
            <div className="col-md-6">
              <label
                className="poppins-semibold"
                style={{
                  fontSize: "12px",
                  color: "#4B5563",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Marks *
              </label>
              <input
                type="number"
                className="form-control rounded-2"
                placeholder="0"
                style={{
                  fontSize: "13px",
                  ...(isMarksMissing ? errorInputStyle : normalInputStyle),
                  padding: "6px 12px",
                }}
                value={question?.correct_score || ""}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setQuestions((prev) => {
                    const updated = {
                      ...prev[nextQuestion],
                      correct_score: val || "",
                      is_question_edited: true,
                    };
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
                min="0"
                max="200"
              />
            </div>
          </div>

          {/* Book & Chapter + Difficulty */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label
                className="poppins-semibold"
                style={{
                  fontSize: "12px",
                  color: "#4B5563",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Book / Chapter *
              </label>
              <select
                className="form-select rounded-2"
                style={{
                  fontSize: "13px",
                  ...(isChapterMissing ? errorInputStyle : normalInputStyle),
                  padding: "6px 12px",
                }}
                onChange={(e) => {
                  setQuestions((prev) => {
                    const updated = {
                      ...prev[nextQuestion],
                      chapter_id: e.target.value,
                      book_id:
                        e.target.options[e.target.selectedIndex].getAttribute(
                          "book-id",
                        ),
                      is_question_edited: true,
                    };
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
                value={question.chapter_id || ""}
              >
                <option value="">Select Book & Chapter</option>
                {chapters?.map((item) =>
                  item.bookInfo.map((book) => (
                    <optgroup label={book.name} key={book.id} book-id={book.id}>
                      {book?.chapterData?.map((chap) => (
                        <option key={chap.id} book-id={book.id} value={chap.id}>
                          {chap.chapterName}
                        </option>
                      ))}
                    </optgroup>
                  )),
                )}
              </select>
            </div>
            <div className="col-md-6">
              <label
                className="poppins-semibold"
                style={{
                  fontSize: "12px",
                  color: "#4B5563",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Difficulty *
              </label>
              <select
                className="form-select rounded-2"
                style={{
                  fontSize: "13px",
                  ...(isDifficultyMissing
                    ? errorInputStyle
                    : normalInputStyle),
                  padding: "6px 12px",
                }}
                onChange={(e) => {
                  setQuestions((prev) => {
                    const updated = {
                      ...prev[nextQuestion],
                      difficulty_level: e.target.value,
                      is_question_edited: true,
                    };
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
                value={question.difficulty_level || ""}
              >
                <option value="">Select Difficulty</option>
                {difficultyLevels &&
                  (selectedBox === "adaptive" ||
                  selectedBox?.adaptive ||
                  selectedBox?.isAdaptive
                    ? difficultyLevels.filter(
                        (level) => level.name !== "None",
                      )
                    : difficultyLevels
                  ).map((level, index) => (
                    <option key={index} value={level.id}>
                      {level.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Bloom's Skill */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label
                className="poppins-semibold"
                style={{
                  fontSize: "12px",
                  color: "#4B5563",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Bloom's Skill *
                <Info
                  size={14}
                  color="#6B7280"
                  style={{ marginLeft: 4, cursor: "pointer" }}
                  onClick={() => setClick(true)}
                />
              </label>
              <select
                className="form-select rounded-2"
                style={{
                  fontSize: "13px",
                  ...(isSkillMissing ? errorInputStyle : normalInputStyle),
                  padding: "6px 12px",
                }}
                onChange={(e) => {
                  const selectedSkill = skillsWithDomainId.find(
                    (s) => s.id === e.target.value,
                  );
                  setQuestions((prev) => {
                    const updated = {
                      ...prev[nextQuestion],
                      skill_id: e.target.value,
                      domain_id: selectedSkill?.domain_id || null,
                      is_question_edited: true,
                    };
                    const newQ = [...prev];
                    newQ[nextQuestion] = updated;
                    return newQ;
                  });
                }}
                value={question.skill_id || ""}
              >
                <option value="">Select Skill</option>
                {(selectedBox === "adaptive" ||
                selectedBox?.adaptive ||
                selectedBox?.isAdaptive
                  ? skillsWithDomainId?.filter((skill) => skill.name !== "None")
                  : skillsWithDomainId
                )?.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ========================================== */}
          {/* QUESTION_BANK — Add to checkboxes (purana logic) */}
          {/* ========================================== */}
          {componentType === ComponentType.QUESTION_BANK && (
            <div className="col-12 mt-2 mb-3 d-flex align-items-start flex-column">
              <div className="col-12 d-flex align-items-start gap-4 flex-wrap">
                {/* Add for Homework */}
                <div className="d-flex align-items-center gap-1">
                  <input
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setQuestions((prev) => {
                        const current = prev[nextQuestion].add_to;
                        let add_to;
                        if (checked)
                          add_to =
                            current === "assessment" ? "all" : "homework";
                        else add_to = current === "all" ? "assessment" : null;
                        const updated = {
                          ...prev[nextQuestion],
                          is_question_edited: true,
                          add_to,
                        };
                        const newQ = [...prev];
                        newQ[nextQuestion] = updated;
                        return newQ;
                      });
                      setUnsavedChanges(true);
                    }}
                    type="checkbox"
                    id="add_to_hw"
                    checked={
                      question?.add_to === "homework" ||
                      question?.add_to === "all"
                    }
                  />
                  <span className="poppins-semibold" style={{ fontSize: "12px" }}>
                    Add for Homework
                  </span>
                </div>

                {/* Add for Assessment */}
                <div className="d-flex align-items-center gap-1">
                  <input
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setQuestions((prev) => {
                        const current = prev[nextQuestion].add_to;
                        let add_to;
                        if (checked)
                          add_to =
                            current === "homework" ? "all" : "assessment";
                        else add_to = current === "all" ? "homework" : null;
                        const updated = {
                          ...prev[nextQuestion],
                          is_question_edited: true,
                          add_to,
                        };
                        const newQ = [...prev];
                        newQ[nextQuestion] = updated;
                        return newQ;
                      });
                      setUnsavedChanges(true);
                    }}
                    type="checkbox"
                    id="add_to_ass"
                    checked={
                      question?.add_to === "assessment" ||
                      question?.add_to === "all"
                    }
                  />
                  <span className="poppins-semibold" style={{ fontSize: "12px" }}>
                    Add for Assessment
                  </span>
                </div>

                {/* Add for Mock */}
                <div className="d-flex align-items-center gap-1">
                  <input
                    onChange={() => {
                      setQuestions((prev) => {
                        const updated = {
                          ...prev[nextQuestion],
                          is_question_edited: true,
                          add_to_mock: !question?.add_to_mock,
                        };
                        const newQ = [...prev];
                        newQ[nextQuestion] = updated;
                        return newQ;
                      });
                      setUnsavedChanges(true);
                    }}
                    id="add_to_qsbank"
                    type="checkbox"
                    checked={question?.add_to_mock}
                  />
                  <span className="poppins-semibold" style={{ fontSize: "12px" }}>
                    Add for Mock
                  </span>
                </div>
              </div>
              <div className="col-12 mt-1 text-warning">
                <small>
                  *Selection will add question to library so that you can
                  re-use it through "Add from School Library" during creation.
                </small>
              </div>
            </div>
          )}

          {/* Render MCQ or Upload Type */}
          {question.question_type === "false" ? (
            <CreateMcq
              nextQuestion={nextQuestion}
              question={question}
              setQuestions={setQuestions}
            />
          ) : (
            question.question_type !== "null" && (
              <CreateUploadType
                nextQuestion={nextQuestion}
                question={question}
                setQuestions={setQuestions}
                componentType={componentType}
                hasDescriptiveAutoAssessment={hasDescriptiveAutoAssessment}
                selectedBox={selectedBox}
              />
            )
          )}
        </div>

        {click && <Guides setClick={setClick} />}
        {showErrorInputField && (
          <CheckValidationInput
            getErrorMsg={getErrorMsg}
            setShowErrorInputField={setShowErrorInputField}
          />
        )}
        {openQuestionsPopup && (
          <QuestionTypePopup setOpenQuestionsPopup={setOpenQuestionsPopup} />
        )}
        {mideaviewer && (
          <PdfViewer
            setMideaviewer={setMideaviewer}
            attachment={responseAttach}
          />
        )}
      </div>

      {/* Zoomed Attachment */}
      {zoomedAttachment && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <button
            onClick={() => setZoomedAttachment(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "transparent",
              border: "none",
              fontSize: "2rem",
              color: "white",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
          <img
            src={zoomedAttachment}
            alt="Zoomed"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
            }}
          />
        </div>
      )}
    </>
  );
};

export default DefaultQuestion;